class NamaslaySearchGallery extends HTMLElement {
  #controller;
  #resizeObserver;
  #mutationObserver;
  #resetPosition = false;
  #track;
  #slides = [];
  #frame = 0;
  #contentDirty = true;
  #activeIndex = -1;
  #counter;
  #pointerStart;
  #dragged = false;

  static observedAttributes = ['data-product-id', 'data-first-image-id'];

  attributeChangedCallback(name, previous, next) {
    if (previous !== null && previous !== next) {
      this.#resetPosition = true;
      this.#scheduleUpdate();
    }
  }

  connectedCallback() {
    this.#contentDirty = true;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('scroll', this.#scheduleUpdate, { capture: true, passive: true, signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { passive: true, signal });
    this.addEventListener('pointermove', this.#onPointerMove, { passive: true, signal });
    this.addEventListener('pointerup', this.#onPointerEnd, { passive: true, signal });
    this.addEventListener('pointercancel', this.#onPointerCancel, { passive: true, signal });
    this.addEventListener('click', this.#onClick, { capture: true, signal });
    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(this);
    this.#mutationObserver = new MutationObserver(() => {
      this.#contentDirty = true;
      this.#scheduleUpdate();
    });
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #scheduleUpdate = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#frame = 0;
    if (this.#contentDirty) {
      this.#track = this.querySelector('[data-gallery-track]');
      this.#slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.#counter = this.querySelector('[data-gallery-current]');
      this.#activeIndex = -1;
      this.#contentDirty = false;
    }
    if (!this.#track?.clientWidth || !this.#slides.length) return;
    if (this.#resetPosition) {
      this.#track.scrollTo({ left: 0, behavior: 'instant' });
      this.#resetPosition = false;
    }
    const index = Math.max(0, Math.min(this.#slides.length - 1, Math.round(this.#track.scrollLeft / this.#track.clientWidth)));
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;
    const counter = this.#counter;
    if (counter && counter.textContent !== String(index + 1)) counter.textContent = String(index + 1);
    this.#slides.forEach((slide, slideIndex) => {
      slide.tabIndex = slideIndex === index ? 0 : -1;
      if (this.hasAttribute('data-gallery-transition')) {
        const image = slide.querySelector('img');
        if (slideIndex === index) image?.setAttribute('ref', 'imagesToTransition[]');
        else image?.removeAttribute('ref');
      }
    });
  };

  #onKeyDown = (event) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !this.#track?.clientWidth) return;
    event.preventDefault();
    const current = Math.round(this.#track.scrollLeft / this.#track.clientWidth);
    const index = Math.max(0, Math.min(this.#slides.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.#slides[index].focus({ preventScroll: true });
    this.#track.scrollTo({ left: index * this.#track.clientWidth, behavior });
  };

  #onPointerDown = (event) => {
    this.#pointerStart = { x: event.clientX, y: event.clientY };
    this.#dragged = false;
  };

  #onPointerMove = (event) => {
    if (!this.#pointerStart) return;
    const dx = Math.abs(event.clientX - this.#pointerStart.x);
    const dy = Math.abs(event.clientY - this.#pointerStart.y);
    if (dx > 8 && dx > dy) this.#dragged = true;
  };

  #onPointerEnd = () => {
    this.#pointerStart = null;
  };

  #onPointerCancel = () => {
    this.#dragged = true;
    this.#onPointerEnd();
  };

  #onClick = (event) => {
    if (event.detail !== 0 && this.#dragged) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
}

if (!customElements.get('namaslay-search-gallery')) {
  customElements.define('namaslay-search-gallery', NamaslaySearchGallery);
}
